import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { FormSection } from '@/components/FormSection';
import { TextField } from '@/components/TextField';
import { TextArea } from '@/components/TextArea';
import { Controller } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { BooleanGroup } from '@/components/BooleanGroup';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { AuditFormData, auditFormSchema } from '@/lib/schemas';
import { getAuditApiUrl } from '@/lib/config';
import { trackAuditCompletion } from '@/lib/analytics';
import { ArrowLeft, Send, CheckCircle, Building2, Workflow, Settings, MessageSquare, BarChart3, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EnumSelect } from '@/components/EnumSelect';

export default function AuditPage() {
  const navigate = useNavigate();
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());
  const [isProgressSticky, setIsProgressSticky] = useState(false);
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // Refs for section navigation
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AuditFormData>({
    resolver: zodResolver(auditFormSchema),
  });

  // Define form sections and their required fields
  const formSections = [
    {
      id: 'company',
      title: 'Company Details',
      icon: Building2,
      fields: ['company_name', 'contact_name', 'email', 'industry', 'admin_staff_count']
    },
    {
      id: 'processes',
      title: 'Processes & Workflows',
      icon: Workflow,
      fields: ['uses_paper', 'double_data_entry', 'has_sops', 'onboarding_time', 'most_time_consuming_tasks']
    },
    {
      id: 'systems',
      title: 'Systems & Tools',
      icon: Settings,
      fields: ['current_systems', 'duplicate_tools', 'integrations_in_place', 'uses_cloud_storage', 'compliance_reporting_automatic']
    },
    {
      id: 'communication',
      title: 'Communication & Collaboration',
      icon: MessageSquare,
      fields: ['uses_email_for_tasks', 'approval_delays', 'cross_dept_visibility', 'client_self_service']
    },
    {
      id: 'data',
      title: 'Data & Reporting',
      icon: BarChart3,
      fields: ['realtime_kpis', 'reporting_automated', 'report_prep_time', 'single_source_of_truth']
    },
    {
      id: 'automation',
      title: 'Automation & AI',
      icon: Zap,
      fields: ['repetitive_tasks_automated', 'uses_ai', 'has_automation_roi_strategy']
    }
  ];

  const watchedValues = watch();

  // Calculate progress
  useEffect(() => {
    const newCompletedSections = new Set<string>();
    
    formSections.forEach(section => {
      const isCompleted = section.fields.every(field => {
        const value = (watchedValues as any)[field];
        return value !== undefined && value !== null && value !== '';
      });
      
      if (isCompleted) {
        newCompletedSections.add(section.id);
      }
    });
    
    setCompletedSections(newCompletedSections);
  }, [watchedValues]);

  const totalSections = formSections.length;
  const completedCount = completedSections.size;
  const progressPercentage = (completedCount / totalSections) * 100;

  // Scroll detection for sticky progress
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsProgressSticky(scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to scroll to section
  const scrollToSection = (sectionId: string) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  const onSubmit = async (data: AuditFormData) => {
    try {
      const response = await fetch(getAuditApiUrl.score(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ form_data: data }),
      });

      if (!response.ok) {
        throw new Error('Failed to process audit');
      }

      const result = await response.json();
      
      // Track audit completion
      trackAuditCompletion(data.company_name, data.industry);
      
      // Store data in sessionStorage to pass to results page
      sessionStorage.setItem('auditData', JSON.stringify(data));
      sessionStorage.setItem('auditResult', JSON.stringify(result));
      
      navigate('/result');
    } catch (error) {
      toast.error('Failed to process audit. Please try again.');
      console.error('Audit submission error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <div className="bg-brand-dark">
        <div className="max-w-6xl mx-auto px-6 py-12">
            <div className="flex items-center gap-6 mb-8">
              <Link to="/">
                <Button 
                  size="sm" 
                  className="text-white bg-brand-orange hover:bg-brand-orange/90 transition-colors duration-200"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  <span className="font-medium">Back to Home</span>
                </Button>
              </Link>
            <div className="flex-1">
              <h1 className="text-responsive-lg font-bold text-white mb-2">
                <span>Digital Efficiency Audit for Aged Care Providers</span>
              </h1>
              <p className="text-lg text-slate-300">Complete 6 short sections to get your personalized assessment and PDF roadmap. Takes ~8-10 minutes</p>
            </div>
          </div>
          
          {/* Progress Indicator */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-brand-dark">Assessment Progress</h3>
              <span className="text-sm text-brand-gray">
                {completedCount} of {totalSections} sections completed
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 mb-4">
              <div 
                className="gradient-advis h-2 rounded-full transition-all duration-500" 
                style={{width: `${progressPercentage}%`}}
              ></div>
            </div>
            
            {/* Section Status */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {formSections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`flex items-center gap-2 text-sm p-3 rounded-lg transition-colors duration-200 cursor-pointer ${
                      completedSections.has(section.id) 
                        ? 'bg-brand-green/10 text-brand-green border border-brand-green/20' 
                        : 'bg-slate-50 text-brand-gray border border-slate-200'
                    }`}
                  >
                    <IconComponent className="h-4 w-4" />
                    <span className="font-medium text-left">{section.title}</span>
                    {completedSections.has(section.id) && (
                      <CheckCircle className="h-4 w-4 text-brand-green ml-auto" />
                    )}
                  </button>
                );
              })}
            </div>
            
            <p className="text-sm text-brand-gray mt-4">
              {completedCount === 0 
                ? 'Start by filling out your company details below' 
                : completedCount === totalSections 
                  ? 'All sections completed! Ready to submit your Advis audit.'
                  : `${totalSections - completedCount} sections remaining`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Sticky Progress Bar */}
      {isProgressSticky && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-semibold text-brand-dark">Assessment Progress</h3>
                <span className="text-sm text-brand-gray">
                  {completedCount} of {totalSections} sections completed
                </span>
              </div>
              <div className="flex items-center gap-1">
                {formSections.map((section) => {
                  const IconComponent = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200 ${
                        completedSections.has(section.id) 
                          ? 'bg-brand-green text-white' 
                          : 'bg-slate-100 text-brand-gray'
                      }`}
                      title={section.title}
                    >
                      <IconComponent className="h-4 w-4" />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-1 mt-2">
              <div 
                className="gradient-advis h-1 rounded-full transition-all duration-500" 
                style={{width: `${progressPercentage}%`}}
              ></div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 py-12">

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Company Details */}
          <div ref={(el) => (sectionRefs.current['company'] = el)}>
            <FormSection 
              title="Company Details" 
              description="Tell us about your organization"
              isCompleted={completedSections.has('company')}
              icon={Building2}
            >
            <div className="grid md:grid-cols-2 gap-8">
              <TextField
                control={control}
                name="company_name"
                label="Company Name"
                placeholder="Enter your company name"
                error={errors.company_name?.message}
              />
              <TextField
                control={control}
                name="contact_name"
                label="Contact Name"
                placeholder="Enter your full name"
                error={errors.contact_name?.message}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <TextField
                control={control}
                name="email"
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                error={errors.email?.message}
              />
              <TextField
                control={control}
                name="industry"
                label="Industry"
                placeholder="e.g., Healthcare, Finance, Manufacturing"
                error={errors.industry?.message}
              />
            </div>
            <div className="space-y-4">
              <Controller
                control={control}
                name="admin_staff_count"
                render={({ field }) => (
                  <div className="space-y-3">
                    <Label className="text-base font-semibold text-brand-dark">
                      How many admin and customer service reps are doing admin/data entry?
                    </Label>
                    <Slider
                      value={[field.value ?? 1]}
                      min={1}
                      max={10}
                      step={1}
                      onValueChange={(value) => field.onChange(value[0])}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-brand-gray">
                      <span>1</span>
                      <span>{field.value ?? 1} team members</span>
                      <span>10+</span>
                    </div>
                    {errors.admin_staff_count && (
                      <p className="text-sm text-red-600 font-medium mt-1">
                        {errors.admin_staff_count.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
            </FormSection>
          </div>

          {/* Processes & Workflows */}
          <div ref={(el) => (sectionRefs.current['processes'] = el)}>
            <FormSection 
              title="Processes & Workflows" 
              description="How does your organization handle day-to-day operations?"
              isCompleted={completedSections.has('processes')}
              icon={Workflow}
            >
            <BooleanGroup
              control={control}
              name="uses_paper"
              label="Does your organization still use paper forms or processes?"
              error={errors.uses_paper?.message}
            />
            <BooleanGroup
              control={control}
              name="double_data_entry"
              label="Do you frequently enter the same data in multiple systems?"
              error={errors.double_data_entry?.message}
            />
            <BooleanGroup
              control={control}
              name="has_sops"
              label="Do you have documented Standard Operating Procedures (SOPs)?"
              error={errors.has_sops?.message}
            />
            <EnumSelect
              control={control}
              name="onboarding_time"
              label="How long does it take to fully onboard a new employee?"
              options={['<1 day', '1–3 days', '>3 days']}
              error={errors.onboarding_time?.message}
            />
            {/* Time-Consuming Tasks: Multi-select checkboxes with Other */}
            <div className="space-y-3">
              <Label className="text-base font-semibold text-brand-dark">What are your most time-consuming tasks?</Label>
              <Controller
                control={control}
                name={"time_consuming_tasks_options" as any}
                defaultValue={[] as any}
                render={({ field }) => {
                  const selected: string[] = (field.value as string[]) || [];
                  const toggle = (val: string) => {
                    if (selected.includes(val)) {
                      field.onChange(selected.filter((v) => v !== val));
                    } else {
                      field.onChange([...selected, val]);
                    }
                  };
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        { value: 'manual_data_entry', label: 'Manual data entry' },
                        { value: 'report_preparation', label: 'Preparing reports' },
                        { value: 'approvals_followups', label: 'Approvals and follow-ups' },
                        { value: 'customer_emails_calls', label: 'Customer emails/calls' },
                        { value: 'other', label: 'Other' },
                      ].map(({ value, label }) => (
                        <label key={value} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors cursor-pointer">
                          <Checkbox
                            checked={selected.includes(value)}
                            onCheckedChange={() => toggle(value)}
                          />
                          <span className="text-brand-dark">{label}</span>
                        </label>
                      ))}
                    </div>
                  );
                }}
              />

              {/* Conditional free-text when Other is selected */}
              <Controller
                control={control}
                name={"time_consuming_tasks_other" as any}
                render={({ field }) => {
                  const selected: string[] = ((watch() as any)["time_consuming_tasks_options"] as string[]) || [];
                  return (
                    <div>
                      {selected.includes('other') && (
                        <Textarea
                          {...field}
                          placeholder="Please specify other time-consuming tasks"
                          rows={3}
                          className="rounded-lg border-slate-200 bg-white text-brand-dark focus:border-brand-orange transition-colors duration-200"
                        />
                      )}
                    </div>
                  );
                }}
              />

              {/* Hidden combined field to preserve existing backend contract */}
              <Controller
                control={control}
                name={"most_time_consuming_tasks" as any}
                render={({ field }) => {
                  const selected: string[] = ((watch() as any)["time_consuming_tasks_options"] as string[]) || [];
                  const other = (watch() as any)["time_consuming_tasks_other"] as string | undefined;
                  const labels: Record<string, string> = {
                    manual_data_entry: 'Manual data entry',
                    report_preparation: 'Preparing reports',
                    approvals_followups: 'Approvals and follow-ups',
                    customer_emails_calls: 'Customer emails/calls',
                    other: other && other.trim().length > 0 ? other.trim() : 'Other',
                  };
                  const combined = selected.map((v) => labels[v]).filter(Boolean).join(', ');
                  if (field.value !== combined) {
                    field.onChange(combined);
                  }
                  return (
                    <input type="hidden" {...field} value={combined} />
                  );
                }}
              />

              {errors.most_time_consuming_tasks?.message && (
                <p className="text-sm text-red-600 mt-2 font-medium">{errors.most_time_consuming_tasks.message}</p>
              )}
            </div>
            </FormSection>
          </div>

          {/* Systems & Tools */}
          <div ref={(el) => (sectionRefs.current['systems'] = el)}>
            <FormSection 
              title="Systems & Tools" 
              description="Tell us about your current technology stack"
              isCompleted={completedSections.has('systems')}
              icon={Settings}
            >
            <TextField
              control={control}
              name="current_systems"
              label="What systems does your organization currently use?"
              placeholder="e.g., Salesforce, Xero, Microsoft 365, custom databases"
              error={errors.current_systems?.message}
            />
            <BooleanGroup
              control={control}
              name="duplicate_tools"
              label="Do you have multiple tools that serve similar purposes?"
              error={errors.duplicate_tools?.message}
            />
            <BooleanGroup
              control={control}
              name="integrations_in_place"
              label="Are your main systems integrated (sharing data automatically)?"
              error={errors.integrations_in_place?.message}
            />
            <BooleanGroup
              control={control}
              name="uses_cloud_storage"
              label="Do you use cloud storage solutions?"
              error={errors.uses_cloud_storage?.message}
            />
            <BooleanGroup
              control={control}
              name="compliance_reporting_automatic"
              label="Is your compliance reporting automated?"
              error={errors.compliance_reporting_automatic?.message}
            />
            </FormSection>
          </div>

          {/* Communication & Collaboration */}
          <div ref={(el) => (sectionRefs.current['communication'] = el)}>
            <FormSection 
              title="Communication & Collaboration" 
              description="How does your team work together?"
              isCompleted={completedSections.has('communication')}
              icon={MessageSquare}
            >
            <BooleanGroup
              control={control}
              name="uses_email_for_tasks"
              label="Do you primarily use email for task management?"
              error={errors.uses_email_for_tasks?.message}
            />
            <BooleanGroup
              control={control}
              name="approval_delays"
              label="Do you experience delays in approval processes?"
              error={errors.approval_delays?.message}
            />
            <BooleanGroup
              control={control}
              name="cross_dept_visibility"
              label="Do different departments have visibility into each other's work?"
              error={errors.cross_dept_visibility?.message}
            />
            <BooleanGroup
              control={control}
              name="client_self_service"
              label="Can clients access information or submit requests without calling/emailing?"
              error={errors.client_self_service?.message}
            />
            </FormSection>
          </div>

          {/* Data & Reporting */}
          <div ref={(el) => (sectionRefs.current['data'] = el)}>
            <FormSection 
              title="Data & Reporting" 
              description="How do you handle data and generate insights?"
              isCompleted={completedSections.has('data')}
              icon={BarChart3}
            >
            <BooleanGroup
              control={control}
              name="realtime_kpis"
              label="Do you have access to real-time KPIs and dashboards?"
              error={errors.realtime_kpis?.message}
            />
            <BooleanGroup
              control={control}
              name="reporting_automated"
              label="Is your regular reporting automated?"
              error={errors.reporting_automated?.message}
            />
            <EnumSelect
              control={control}
              name="report_prep_time"
              label="How much time do you spend preparing reports each week?"
              options={['<1 hr/week', '1-5 hrs/week', '>5 hrs/week']}
              error={errors.report_prep_time?.message}
            />
            <BooleanGroup
              control={control}
              name="single_source_of_truth"
              label="Do you have a single source of truth for your data?"
              error={errors.single_source_of_truth?.message}
            />
            </FormSection>
          </div>

          {/* Automation & AI */}
          <div ref={(el) => (sectionRefs.current['automation'] = el)}>
            <FormSection 
              title="Automation & AI" 
              description="How automated are your processes?"
              isCompleted={completedSections.has('automation')}
              icon={Zap}
            >
            <BooleanGroup
              control={control}
              name="repetitive_tasks_automated"
              label="Are your repetitive tasks automated?"
              error={errors.repetitive_tasks_automated?.message}
            />
            <BooleanGroup
              control={control}
              name="uses_ai"
              label="Does your organization currently use AI in any capacity?"
              error={errors.uses_ai?.message}
            />
            <BooleanGroup
              control={control}
              name="has_automation_roi_strategy"
              label="Do you have a strategy for measuring automation ROI?"
              error={errors.has_automation_roi_strategy?.message}
            />
            </FormSection>
          </div>

          {/* Pain Points */}
          <FormSection 
            title="Current Challenges" 
            description="Help us understand your biggest pain points"
          >
            <TextArea
              control={control}
              name="top_pain_points"
              label="What are your top 3 pain points? (Optional)"
              placeholder="Describe the biggest challenges your organization faces daily"
              error={errors.top_pain_points?.message}
            />
            <TextArea
              control={control}
              name="wish_list"
              label="If you could change anything, what would it be? (Optional)"
              placeholder="Describe your ideal state or what you wish you had"
              error={errors.wish_list?.message}
            />
          </FormSection>

        <div className="flex justify-center">
          <Button
            id="btn_submit_audit"
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="w-full sm:w-auto border-brand-orange/30 text-brand-orange bg-gradient-to-br from-orange-50 to-orange-100 hover:bg-gradient-to-br hover:from-brand-orange hover:to-brand-orange/90 hover:text-white hover:border-brand-orange px-6 py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
          >
              {isSubmitting ? (
                'Processing...'
              ) : (
                <>
                  <Send className="mr-2 h-5 w-5" />
                  Get My Audit Results
                </>
              )}
            </Button>
          </div>          
          <p className="text-sm text-brand-gray text-center mt-4">
            We never share your data. You'll get a PDF + 1 follow-up email
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] items-center bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
            <p className="text-sm sm:text-base text-brand-dark leading-relaxed">
              Need implementation help? Book a 20-mins walkthrough to priorities the quick wins.
            </p>
            <div className="flex justify-center sm:justify-end">
              <a
                href="https://cal.com/advissoftware/digitalisation-audit-consultation"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  id="btn_book_walkthrough_bottom"
                  type="button"
                  size="lg"
                  className="mx-auto inline-flex items-center justify-center gap-3 rounded-xl bg-[#34a853] px-8 py-3 text-lg font-semibold text-white shadow-[0_14px_30px_-12px_rgba(52,168,83,0.65)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#2f984d] hover:shadow-[0_20px_40px_-16px_rgba(52,168,83,0.7)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#60c145]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                >
                  Book a 20-mins walkthrough
                </Button>
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
